/**
 * ADR-0185 Phase A — the lane-expansion memory: which machines are expanded
 * (one, several, or all), remembered per browser per model. House pattern:
 * dotted atlas.* key, JSON value, every storage access behind try/catch (a
 * blocked or cleared store must never break the board).
 *
 * "All" is stored as a flag, not an enumeration, so Expand all keeps meaning
 * ALL when tomorrow's book brings a machine today's didn't have.
 */

export interface ExpandedLanes {
  all: boolean;
  machines: string[];
}

export function expandStorageKey(modelId: string): string {
  return `atlas.optimizeBoard.expandedLanes.${modelId}`;
}

/** Parse a stored value; null (fall back to collapsed) on anything malformed. */
export function parseExpandedLanes(raw: string | null): ExpandedLanes | null {
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw) as { all?: unknown; machines?: unknown };
    if (typeof parsed !== "object" || parsed === null) return null;
    const all = parsed.all === true;
    const machines = Array.isArray(parsed.machines)
      ? parsed.machines.filter((m): m is string => typeof m === "string")
      : [];
    return { all, machines };
  } catch {
    return null;
  }
}

export function serializeExpandedLanes(state: ExpandedLanes): string {
  return JSON.stringify({ all: state.all, machines: state.machines });
}

export function loadExpandedLanes(modelId: string): ExpandedLanes | null {
  try {
    return parseExpandedLanes(window.localStorage.getItem(expandStorageKey(modelId)));
  } catch {
    return null;
  }
}

export function saveExpandedLanes(modelId: string, state: ExpandedLanes): void {
  try {
    window.localStorage.setItem(expandStorageKey(modelId), serializeExpandedLanes(state));
  } catch {
    /* per-browser convenience only — losing it costs a click */
  }
}

/** The set of machines to draw expanded, resolved against the drawn lanes. */
export function expandedSetFor(state: ExpandedLanes | null, machines: string[]): Set<string> {
  if (!state) return new Set();
  if (state.all) return new Set(machines);
  return new Set(state.machines.filter((m) => machines.includes(m)));
}

/** Toggle one machine, collapsing the "all" flag into an enumeration first. */
export function toggleMachine(state: ExpandedLanes | null, machines: string[], machine: string): ExpandedLanes {
  const current = expandedSetFor(state, machines);
  if (current.has(machine)) current.delete(machine);
  else current.add(machine);
  return { all: machines.length > 0 && machines.every((m) => current.has(m)), machines: [...current] };
}
